// M2.47 — Importar plan del entrenador: pegar texto libre → Claude lo
// estructura → preview editable → bulk create vía POST /api/workouts/planned.
//
// Flujo:
//   1. Estado 'input': textarea grande con texto pegado + datepicker de
//      "fecha de referencia" (para resolver "lunes" como fecha concreta).
//      Botón "Analizar con IA".
//   2. Estado 'preview': lista de entradas con checkbox + edición inline
//      (fecha, hora, disciplina, duración, intensidad, descripción).
//      Botón "Crear N entrenos".
//   3. Estado 'creating': barra de progreso (X de N), cada uno via
//      useCreatePlannedWorkout en serie.
//   4. Estado 'done': resumen + cerrar.
//
// Sin tocar la BD del usuario: el endpoint backend NO persiste; los
// inserts pasan SOLO si el usuario pulsa "Crear entrenos".

import {
  useState,
  type ReactElement,
} from 'react';
import { X, Sparkles, Trash2 } from 'lucide-react';
import {
  useParsePlanImport,
  type ParsePlanEntry,
} from '@/lib/coach';
import {
  INTENSITY_ZONES,
  PLANNED_DISCIPLINES,
  useCreatePlannedWorkout,
  type IntensityZone,
  type PlannedDiscipline,
} from '@/lib/workouts';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';

// ---------------------------------------------------------------------------
// Tipos locales
// ---------------------------------------------------------------------------

/**
 * Entrada editada por el usuario antes de crear. Misma shape que
 * `ParsePlanEntry` + flag `include` (checkbox del usuario).
 */
interface EditableEntry extends ParsePlanEntry {
  include: boolean;
  // ID local para `key={}` estable mientras se edita
  uid: string;
}

type Stage = 'input' | 'preview' | 'creating' | 'done';

export function ImportPlanModal({
  defaultReferenceDate,
  onClose,
}: {
  defaultReferenceDate?: Date;
  onClose: () => void;
}): ReactElement {
  const [stage, setStage] = useState<Stage>('input');
  const [text, setText] = useState<string>('');
  const [referenceDate, setReferenceDate] = useState<string>(() =>
    toISODateLocal(defaultReferenceDate ?? new Date()),
  );
  const [entries, setEntries] = useState<EditableEntry[]>([]);
  const [parseNotes, setParseNotes] = useState<string | null>(null);
  const [parseTimezone, setParseTimezone] = useState<string>('Europe/Madrid');
  const [createProgress, setCreateProgress] = useState<{
    done: number;
    total: number;
    failed: number;
  }>({ done: 0, total: 0, failed: 0 });
  const [error, setError] = useState<string | null>(null);

  const parseMutation = useParsePlanImport();
  const createMutation = useCreatePlannedWorkout();

  // -- Acciones -----------------------------------------------------------

  async function handleAnalyze() {
    setError(null);
    if (text.trim().length < 10) {
      setError('Pega al menos 10 caracteres del plan.');
      return;
    }
    try {
      const refISO = new Date(`${referenceDate}T12:00:00Z`).toISOString();
      const result = await parseMutation.mutateAsync({
        text: text.trim(),
        referenceDateISO: refISO,
      });
      if (result.entries.length === 0) {
        setError(
          'La IA no detectó sesiones en el texto. ' +
            (result.notes ? `Nota: ${result.notes}` : ''),
        );
        return;
      }
      setEntries(
        result.entries.map((e, i) => ({
          ...e,
          include: true,
          uid: `${Date.now()}-${i}`,
        })),
      );
      setParseNotes(result.notes);
      setParseTimezone(result.timezone);
      setStage('preview');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No se pudo analizar el plan. Revisa la configuración de Anthropic.',
      );
    }
  }

  async function handleCreateAll() {
    const toCreate = entries.filter((e) => e.include);
    if (toCreate.length === 0) {
      setError('Selecciona al menos un entreno para crear.');
      return;
    }
    setStage('creating');
    setError(null);
    setCreateProgress({ done: 0, total: toCreate.length, failed: 0 });

    let done = 0;
    let failed = 0;
    for (const e of toCreate) {
      try {
        const datetime = composeISO(e.date, e.time);
        await createMutation.mutateAsync({
          datetime,
          timezone: parseTimezone,
          discipline: e.discipline as PlannedDiscipline,
          durationPlannedMin: e.durationPlannedMin,
          intensityZone: e.intensityZone as IntensityZone | null,
          description: e.description.trim() === '' ? null : e.description.trim(),
        });
        done++;
      } catch {
        failed++;
      }
      setCreateProgress({ done: done + failed, total: toCreate.length, failed });
    }
    setStage('done');
  }

  function updateEntry(uid: string, patch: Partial<EditableEntry>) {
    setEntries((prev) =>
      prev.map((e) => (e.uid === uid ? { ...e, ...patch } : e)),
    );
  }

  function removeEntry(uid: string) {
    setEntries((prev) => prev.filter((e) => e.uid !== uid));
  }

  // -- Render -------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && stage !== 'creating') onClose();
      }}
      data-testid="import-plan-modal-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-plan-title"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div>
            <h2
              id="import-plan-title"
              className="text-lg font-semibold text-slate-900"
            >
              Importar plan del entrenador
            </h2>
            <p className="mt-0.5 text-xs text-slate-600">
              {stage === 'input' &&
                'Pega el plan (WhatsApp, Excel, PDF, lo que sea) y la IA lo estructura.'}
              {stage === 'preview' &&
                'Revisa, edita y elige qué sesiones crear. Las marcadas en rojo necesitan revisión.'}
              {stage === 'creating' && 'Creando entrenos uno a uno…'}
              {stage === 'done' && 'Resumen de la importación.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={stage === 'creating'}
            aria-label="Cerrar"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4">
          {stage === 'input' && (
            <InputStage
              text={text}
              setText={setText}
              referenceDate={referenceDate}
              setReferenceDate={setReferenceDate}
              isAnalyzing={parseMutation.isPending}
              onAnalyze={handleAnalyze}
              error={error}
            />
          )}
          {stage === 'preview' && (
            <PreviewStage
              entries={entries}
              parseNotes={parseNotes}
              onChange={updateEntry}
              onRemove={removeEntry}
              error={error}
            />
          )}
          {stage === 'creating' && <CreatingStage progress={createProgress} />}
          {stage === 'done' && (
            <DoneStage
              progress={createProgress}
              onClose={onClose}
              onBackToPreview={() => setStage('preview')}
            />
          )}
        </div>

        {/* Footer */}
        {(stage === 'input' || stage === 'preview') && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            {stage === 'input' && (
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={parseMutation.isPending || text.trim().length < 10}
                className="inline-flex items-center gap-2 rounded bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {parseMutation.isPending ? 'Analizando…' : 'Analizar con IA'}
              </button>
            )}
            {stage === 'preview' && (
              <button
                type="button"
                onClick={handleCreateAll}
                disabled={entries.filter((e) => e.include).length === 0}
                className="rounded bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                Crear {entries.filter((e) => e.include).length} entrenos
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------

function InputStage({
  text,
  setText,
  referenceDate,
  setReferenceDate,
  isAnalyzing,
  onAnalyze: _onAnalyze,
  error,
}: {
  text: string;
  setText: (v: string) => void;
  referenceDate: string;
  setReferenceDate: (v: string) => void;
  isAnalyzing: boolean;
  onAnalyze: () => void;
  error: string | null;
}): ReactElement {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">
            Fecha de referencia (para resolver "lunes" → fecha concreta)
          </span>
          <input
            type="date"
            value={referenceDate}
            onChange={(e) => setReferenceDate(e.target.value)}
            disabled={isAnalyzing}
            className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600 sm:w-auto"
          />
          <span className="mt-1 block text-xs text-slate-500">
            Si tu plan dice "lunes, martes...", la IA usa esta fecha como
            ancla. Por defecto: hoy.
          </span>
        </label>
      </div>

      <div>
        <label className="block text-sm">
          <span className="font-medium text-slate-700">
            Pega el plan del entrenador
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Ejemplo:\n\nSemana del 16 al 22 de junio:\n- Lunes: 1h bici Z2 + 30 min core\n- Martes: descanso\n- Miércoles: 8x400m en pista al 90% (con 90s recuperación)\n- Jueves: 45 min rodaje suave\n- Viernes: piscina 2000m técnica\n- Sábado: 3h bici larga + 20 min carrera (brick)\n- Domingo: 1h30 rodaje long`}
            disabled={isAnalyzing}
            maxLength={20000}
            rows={12}
            className="mt-1 block w-full resize-y rounded-md border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
          />
          <span className="mt-1 block text-xs text-slate-500">
            {text.length} / 20 000 caracteres. Cualquier formato: lista, tabla,
            mensaje, párrafo libre.
          </span>
        </label>
      </div>

      {error && (
        <p role="alert" className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {isAnalyzing && (
        <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
          La IA está analizando el plan… (10-30 segundos)
        </p>
      )}
    </div>
  );
}

function PreviewStage({
  entries,
  parseNotes,
  onChange,
  onRemove,
  error,
}: {
  entries: EditableEntry[];
  parseNotes: string | null;
  onChange: (uid: string, patch: Partial<EditableEntry>) => void;
  onRemove: (uid: string) => void;
  error: string | null;
}): ReactElement {
  return (
    <div className="space-y-3">
      {parseNotes && (
        <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <strong>Notas de la IA:</strong> {parseNotes}
        </p>
      )}
      {error && (
        <p role="alert" className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="text-sm text-slate-700">
        Detectadas <strong>{entries.length}</strong> sesiones. Desmarca las que
        no quieras crear o edítalas inline.
      </p>

      <div className="space-y-2">
        {entries.map((e) => (
          <EntryRow
            key={e.uid}
            entry={e}
            onChange={(patch) => onChange(e.uid, patch)}
            onRemove={() => onRemove(e.uid)}
          />
        ))}
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  onChange,
  onRemove,
}: {
  entry: EditableEntry;
  onChange: (patch: Partial<EditableEntry>) => void;
  onRemove: () => void;
}): ReactElement {
  const lowConf = entry.confidence === 'low';
  const borderCls = !entry.include
    ? 'border-slate-200 bg-slate-50 opacity-60'
    : lowConf
      ? 'border-rose-300 bg-rose-50'
      : entry.confidence === 'medium'
        ? 'border-amber-300 bg-amber-50'
        : 'border-slate-200 bg-white';

  return (
    <div className={`rounded border ${borderCls} p-3`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={entry.include}
          onChange={(e) => onChange({ include: e.target.checked })}
          aria-label="Incluir este entreno"
          className="mt-1.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
        />
        <div className="flex-1">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <input
              type="date"
              value={entry.date}
              onChange={(e) => onChange({ date: e.target.value })}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <input
              type="time"
              value={entry.time}
              onChange={(e) => onChange({ time: e.target.value })}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <select
              value={entry.discipline}
              onChange={(e) =>
                onChange({ discipline: e.target.value as PlannedDiscipline })
              }
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            >
              {PLANNED_DISCIPLINES.map((d) => (
                <option key={d} value={d}>
                  {disciplineLabel(d)}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={10}
              max={600}
              step={5}
              value={entry.durationPlannedMin}
              onChange={(e) =>
                onChange({ durationPlannedMin: Number(e.target.value) || 60 })
              }
              className="rounded border border-slate-300 px-2 py-1 text-xs"
              aria-label="Duración minutos"
            />
            <select
              value={entry.intensityZone ?? ''}
              onChange={(e) =>
                onChange({
                  intensityZone:
                    e.target.value === ''
                      ? null
                      : (e.target.value as IntensityZone),
                })
              }
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            >
              <option value="">—</option>
              {INTENSITY_ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </div>
          <textarea
            value={entry.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={2}
            className="mt-2 block w-full resize-y rounded border border-slate-300 px-2 py-1 text-xs"
            placeholder="Descripción / notas del entrenador"
          />
          {lowConf && (
            <p className="mt-1 text-[11px] font-medium text-rose-700">
              ⚠ La IA tuvo dudas — revisa fecha, duración y disciplina.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Quitar de la lista"
          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-rose-700"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function CreatingStage({
  progress,
}: {
  progress: { done: number; total: number; failed: number };
}): ReactElement {
  const pct = progress.total === 0 ? 0 : (progress.done / progress.total) * 100;
  return (
    <div className="py-8 text-center">
      <p className="text-sm font-medium text-slate-700">
        Creando {progress.done} / {progress.total} entrenos…
      </p>
      <div className="mt-3 h-2 w-full rounded bg-slate-200">
        <div
          className="h-2 rounded bg-teal-600 transition-all"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={progress.done}
          aria-valuemin={0}
          aria-valuemax={progress.total}
        />
      </div>
      {progress.failed > 0 && (
        <p className="mt-3 text-xs text-rose-700">
          {progress.failed} fallaron — se reintentarán al final.
        </p>
      )}
    </div>
  );
}

function DoneStage({
  progress,
  onClose,
  onBackToPreview,
}: {
  progress: { done: number; total: number; failed: number };
  onClose: () => void;
  onBackToPreview: () => void;
}): ReactElement {
  const ok = progress.done - progress.failed;
  return (
    <div className="space-y-4 py-4 text-center">
      <p className="text-lg font-semibold text-emerald-700">
        ✓ {ok} entrenos creados
      </p>
      {progress.failed > 0 && (
        <p className="text-sm text-rose-700">
          {progress.failed} fallaron — revisa el log del backend.
        </p>
      )}
      <p className="text-xs text-slate-600">
        Ya están en tu calendario. Puedes editarlos como cualquier otro
        entreno.
      </p>
      <div className="flex justify-center gap-2">
        <button
          type="button"
          onClick={onBackToPreview}
          className="rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Volver al preview
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toISODateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function composeISO(date: string, time: string): string {
  const local = new Date(`${date}T${time}:00`);
  const off = -local.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${date}T${time}:00${sign}${hh}:${mm}`;
}
